import React, { useState, Fragment } from 'react';
import { Minus, Plus } from 'lucide-react';
interface FAQItem {
  question: string;
  answer: string;
}
interface FAQSectionProps {
  'data-id'?: string;
  title?: string;
  subtitle?: string;
  description?: string;
  ctaText?: string;
  ctaSubtext?: string;
  ctaButtonText?: string;
  ctaButtonHref?: string;
  faqs?: FAQItem[];
}
export const FAQSection: React.FC<FAQSectionProps> = ({
  'data-id': dataId,
  title = "Got questions?\nWe've got answers.",
  subtitle = 'FAQs',
  description = 'Everything you need to know before automating your MLS deal flow.',
  ctaText = 'Still have questions?',
  ctaSubtext = "We'll walk you through setup and your first campaign.",
  ctaButtonText = 'Chat with our team',
  ctaButtonHref = './contact',
  faqs = [{
    question: '1. How quickly can I start using AcquireFlow?',
    answer: "Setup takes less than an hour. Just connect your email, add your phone number through Twilio, and set your offer filters. Once that's done, AcquireFlow starts sending offers automatically — no tech skills required."
  }, {
    question: '2. Do I need MLS access to use AcquireFlow?',
    answer: 'Nope. AcquireFlow is fully approved and authorized to pull live MLS data for you. That means you can start sourcing and sending offers instantly — no agent login or MLS credentials needed.'
  }, {
    question: '3. How does AcquireFlow decide which properties to send offers on?',
    answer: "You're in control. Set your own filters — like property type, price range, days on market, or keywords — and send bulk offers with one click. AcquireFlow applies your rules automatically so every offer fits your buy box."
  }, {
    question: '4. Can I use AcquireFlow for multiple markets or team members?',
    answer: 'Absolutely. The Professional plan supports up to 3 target markets, while Enterprise gives you unlimited markets, users, and full team collaboration.'
  }, {
    question: '5. Will my offers actually get responses?',
    answer: 'Yes. AcquireFlow tracks every agent open, reply, and counteroffer in real time — plus automates follow-ups so you never miss a deal. Most users start getting responses within days.'
  }, {
    question: "6. What's included in my plan?",
    answer: 'Every plan includes AI property analysis, automated offer generation, bulk sending, response tracking, and deal pipeline analytics. Higher tiers unlock white-label tools, API access, and dedicated support.'
  }]
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  return <section id="faq" className="relative w-full px-6 md:px-8 py-24 md:py-32 overflow-hidden" data-id="element-48">
      <div className="relative max-w-[1280px] mx-auto flex flex-col lg:flex-row gap-12 lg:gap-16 z-10" data-id="element-49">
        {/* Left Column */}
        <div className="flex-[4] flex flex-col justify-between gap-12" data-id="element-50">
          <div className="flex flex-col gap-6" data-id="element-51">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3ab795]/10 border border-[#3ab795]/20 backdrop-blur-sm w-fit" data-id="element-52">
              <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-53" />
              <p className="text-sm md:text-base font-medium text-[#3ab795] tracking-wide uppercase" data-id="element-54">
                {subtitle}
              </p>
            </div>
            {/* Title */}
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#1D1D1D] leading-tight tracking-tight max-w-[540px]" data-id="element-55">
              {title.split('\n').map((line, i) => <Fragment key={i} data-id="element-56">
                  {line}
                  {i < title.split('\n').length - 1 && <br data-id="element-57" />}
                </Fragment>)}
            </h2>
            {/* Description */}
            <p className="text-lg text-gray-600 max-w-[420px] leading-relaxed" data-id="element-58">
              {description}
            </p>
          </div>
          {/* CTA Card */}
          <div className="bg-[#f8f9fa] border border-gray-200 rounded-2xl p-6 max-w-[400px] shadow-lg" data-id="element-59">
            <div className="flex flex-col gap-4" data-id="element-60">
              <div data-id="element-61">
                <h5 className="text-xl font-semibold text-[#1D1D1D] tracking-tight mb-2" data-id="element-62">
                  {ctaText}
                </h5>
                <p className="text-lg text-gray-600 leading-relaxed" data-id="element-63">
                  {ctaSubtext}
                </p>
              </div>
              <a href={ctaButtonHref} className="inline-flex items-center justify-center gap-2 bg-[#3ab795] hover:bg-[#2d9575] text-white font-semibold text-base px-6 py-3 rounded-xl shadow-[0_4px_16px_rgba(58,183,149,0.3)] hover:shadow-[0_8px_24px_rgba(58,183,149,0.4)] transition-all hover:scale-105 w-fit" data-id="element-64">
                {ctaButtonText}
                <svg width="16" height="16" viewBox="0 0 256 256" fill="currentColor" data-id="element-65">
                  <path d="M224.49,136.49l-72,72a12,12,0,0,1-17-17L187,140H40a12,12,0,0,1,0-24H187L135.51,64.48a12,12,0,0,1,17-17l72,72A12,12,0,0,1,224.49,136.49Z" data-id="element-66" />
                </svg>
              </a>
            </div>
          </div>
        </div>
        {/* Right Column - FAQ Accordion */}
        <div className="flex-[5] flex flex-col gap-6" data-id="element-67">
          {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} isOpen={openIndex === index} onToggle={() => toggleFAQ(index)} data-id="element-68" />)}
        </div>
      </div>
    </section>;
};
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}
const FAQItem: React.FC<FAQItemProps> = ({
  question,
  answer,
  isOpen,
  onToggle
}) => {
  return <div className="bg-[#f8f9fa] border border-gray-200 rounded-[20px] p-5 shadow-md transition-all duration-300 cursor-pointer hover:shadow-lg hover:border-[#3ab795]/30" onClick={onToggle} data-id="element-69">
      <div className="flex flex-col gap-3" data-id="element-70">
        {/* Question Header */}
        <div className="flex items-center justify-between gap-5" data-id="element-71">
          <h3 className="text-lg font-semibold text-[#1D1D1D] tracking-tight flex-1" data-id="element-72">
            {question}
          </h3>
          <div className="flex-shrink-0 w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center transition-colors hover:bg-gray-200" data-id="element-73">
            {isOpen ? <Minus className="w-5 h-5 text-[#1D1D1D]" data-id="element-74" /> : <Plus className="w-5 h-5 text-[#1D1D1D]" data-id="element-75" />}
          </div>
        </div>
        {/* Answer */}
        <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-76">
          <p className="text-base text-gray-600 leading-relaxed pr-5" data-id="element-77">
            {answer}
          </p>
        </div>
      </div>
    </div>;
};